import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney, CURRENCY_META } from "@/lib/api";
import { useI18n } from "@/i18n";
import { ArrowRight, ArrowLeft, Plus, XCircle, Loader2, ArrowUpRight, ArrowDownLeft, Copy } from "lucide-react";

function txnIcon(t) {
  const credit = ["deposit", "transfer_in", "exchange_in", "receive"].includes(t);
  return credit
    ? <ArrowDownLeft size={16} className="text-emerald-400" />
    : <ArrowUpRight size={16} className="text-red-400" />;
}

const TXN_LABEL = {
  ar: { deposit: "إيداع", withdraw: "سحب", transfer_in: "تحويل وارد", transfer_out: "تحويل صادر", exchange_in: "تحويل عملة وارد", exchange_out: "تحويل عملة صادر", send: "إرسال", receive: "استقبال" },
  en: { deposit: "Deposit", withdraw: "Withdraw", transfer_in: "Transfer in", transfer_out: "Transfer out", exchange_in: "FX in", exchange_out: "FX out", send: "Sent", receive: "Received" },
};

export default function AccountDetail() {
  const { id } = useParams();
  const { t, lang, dir } = useI18n();
  const navigate = useNavigate();
  const [acc, setAcc] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositAmount, setDepositAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, tr, c] = await Promise.all([
        api.get(`/accounts/${id}`), api.get(`/accounts/${id}/transactions`), api.get("/currencies"),
      ]);
      setAcc(a.data); setTxns(tr.data);
      setCurrency(c.data.find((x) => x.code === a.data.currency_code));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const dec = currency?.decimals ?? 2;

  const deposit = async () => {
    const amt = parseFloat(depositAmount);
    if (!amt || amt <= 0) return toast.error(t("tr.badAmount"));
    setBusy(true);
    try {
      await api.post("/operations/deposit", { account_id: id, amount: amt });
      toast.success(t("acct.deposited")); setDepositAmount(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const closeAccount = async () => {
    if (!window.confirm(t("acct.closeConfirm"))) return;
    try { await api.post(`/accounts/${id}/close`); toast.success(t("acct.closed")); navigate("/"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success(t("common.copied")); };

  if (loading || !acc) return <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900 mx-auto mt-20" />;

  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
        <BackIcon size={16} /> {t("common.back")}
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <div className="ob-card rounded-md p-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-4xl">{CURRENCY_META[acc.currency_code]?.flag || "🏦"}</span>
              <div>
                <div className="font-mono-ob text-[#00E5FF] text-lg font-bold">{acc.currency_code}</div>
                <div className="text-sm text-zinc-400">{lang === "ar" ? currency?.name_ar : currency?.name}</div>
              </div>
            </div>
            <div className="text-xs text-zinc-500">{t("dash.available")}</div>
            <div className="font-num text-3xl font-extrabold text-white mb-1" dir="ltr" data-testid="detail-balance">
              {fmtMoney(acc.available_balance, dec)} <span className="text-base text-[#00E5FF]">{currency?.symbol}</span>
            </div>
            <div className="text-xs text-amber-400 mb-5" dir="ltr">{t("acct.pendingBal")}: {fmtMoney(acc.pending_balance, dec)}</div>

            <div className="space-y-2 text-sm">
              {[
                [t("dash.accountNo"), acc.account_number],
                [t("acct.walletNo"), acc.wallet_number],
                ["IBAN", acc.iban],
              ].map(([label, val]) => (
                <div key={label} className="flex items-center justify-between gap-2 py-1.5 border-b border-zinc-900">
                  <span className="text-zinc-500 text-xs shrink-0">{label}</span>
                  <span className="font-mono-ob text-zinc-300 text-xs truncate" dir="ltr">{val}</span>
                  <button onClick={() => copy(val)} className="text-zinc-600 hover:text-[#00E5FF]"><Copy size={13} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="ob-card rounded-md p-6 text-center" data-testid="qr-section">
            <div className="text-xs font-semibold tracking-widest text-zinc-400 uppercase mb-3">{t("acct.qrTitle")}</div>
            <img src={acc.qr_code} alt="QR" className="w-44 h-44 mx-auto rounded-md border border-zinc-800" />
            <p className="text-xs text-zinc-500 mt-3">{t("acct.qrSub")}</p>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="ob-card rounded-md p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Plus size={18} /> {t("acct.deposit")}</h3>
            <div className="flex gap-3">
              <input type="number" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} dir="ltr"
                placeholder="0.00" data-testid="deposit-amount-input"
                className="flex-1 bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white text-end focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] outline-none" />
              <button onClick={deposit} disabled={busy} data-testid="deposit-submit"
                className="bg-[#00E5FF] text-black font-bold px-6 rounded-md hover:bg-[#33EBFF] transition-colors flex items-center gap-2 disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />} {t("acct.deposit.btn")}
              </button>
            </div>
            <button onClick={closeAccount} data-testid="close-account-button"
              className="mt-4 text-sm text-red-400 hover:text-red-300 flex items-center gap-1">
              <XCircle size={15} /> {t("acct.close")}
            </button>
          </div>

          <div className="ob-card rounded-md p-6">
            <h3 className="text-lg font-bold text-white mb-4">{t("acct.statement")}</h3>
            {txns.length === 0 ? (
              <p className="text-zinc-500 text-sm py-8 text-center">{t("acct.noTxns")}</p>
            ) : (
              <div className="space-y-1" data-testid="transactions-list">
                {txns.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between py-3 border-b border-zinc-900 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center">{txnIcon(tx.type)}</div>
                      <div>
                        <div className="text-sm text-white">{TXN_LABEL[lang][tx.type] || tx.type}</div>
                        <div className="text-xs text-zinc-500" dir="ltr">{tx.counterparty || ""} · {new Date(tx.created_at).toLocaleString("en-GB")}</div>
                      </div>
                    </div>
                    <div className="text-end">
                      <div className={`font-num font-semibold ${tx.amount >= 0 ? "text-emerald-400" : "text-red-400"}`} dir="ltr">
                        {tx.amount >= 0 ? "+" : ""}{fmtMoney(tx.amount, dec)}
                      </div>
                      {tx.exchange_rate_used && <div className="text-[10px] text-zinc-600" dir="ltr">@ {tx.exchange_rate_used}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
